import { useEffect, useState, useRef } from 'react'
import { DocumentArrowDownIcon, PlusIcon, PencilSquareIcon, TrashIcon, PrinterIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline'
import TableComponent from 'components/common/TableComponent'
import { makeRequest } from 'services/request.service'
import { useAuth } from 'auth/AuthContext'
import ButtonComponent from 'components/common/ButtonComponent'
import StickersModal from 'components/modals/StickersModal'
import ImportStickersModal from 'components/modals/ImportStickersModal'
import ActionCardComponent from 'components/common/ActionCardComponent'
import { stickerTypes, reusePolicies } from 'constants/stickers'
import { removeEmpty } from 'utils/objectUtils'
import { showAlert } from 'services/alerts.service'
import PageLoadingState from 'components/common/PageLoadingState'
import { exportDataEditExcel, exportDataEditPdf, filterRowsBySearch } from 'utils/dataEditExport'

const stickerExportColumns = [
  { key: 'stickerId', label: '#' },
  { key: 'workerName', label: 'Worker' },
  { key: 'containerName', label: 'Container' },
  { key: 'containerMaterial', label: 'Material' },
  { key: 'grossWeight', label: 'Wgt/Unit (kg)' },
  { key: 'reusePolicy', label: 'Reuse' },
  { key: 'resetEvery', label: 'Reset' },
  { key: 'resetTime', label: 'Reset Time' },
  { key: 'ranges', label: 'Ranges' },
  { key: 'quantity', label: 'Qty' },
  { key: 'stickerType', label: 'Type' },
  { key: 'secondsToClose', label: 'Close (s)' }
]

export default function Stickers () {
  const hasFetched = useRef(false)
  const { user } = useAuth()

  const [state, setState] = useState({
    selectedSticker: null,
    stickers: [],
    containers: [],
    workers: [],
    isOpenStickersModal: false,
    isOpenImportModal: false,
    filter: '',
    refresh: 0,
    searchTerm: '',
    isImporting: false,
    loading: true
  })

  const getStickers = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/stickers/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      })

      setState((s) => ({ ...s, stickers: response.data }))
    } catch (error) {
      setState((s) => ({ ...s, stickers: [] }))
    }
  }

  const getContainers = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: '/management/containers',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      })
      setState((s) => ({ ...s, containers: response.data }))
    } catch (error) {
      setState((s) => ({ ...s, containers: [] }))
    }
  }

  const getWorkers = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/workers-management/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      })
      setState((s) => ({ ...s, workers: response.data }))
    } catch (error) {
      setState((s) => ({ ...s, workers: [] }))
    }
  }

  const onSave = async (formData) => {
    formData = removeEmpty(formData)
    try {
      await makeRequest({
        method: 'POST',
        url: `/management/stickers/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        },
        data: formData,
        alertResult: true
      })

      // refrech stickers to refresh the table
      await getStickers()

      toggleStickersModal()
    } catch (error) {
      setState((s) => ({ ...s, selectedSticker: formData }))
      showAlert({
        text: error.response?.data?.message || 'Error creating sticker',
        icon: 'error'
      })
      toggleStickersModal()
    }
  }

  const handlePrintSticker = async (sticker) => {
    try {
      const token = localStorage.getItem('secret')
      const response = await makeRequest({
        method: 'GET',
        url: `/management/stickers/${sticker.stickerId}/print`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
      })
      const blob = response.data
      // Try to get filename from Content-Disposition header if available
      let filename = `sticker_${sticker.stickerId}.pdf`
      const disposition = response.headers && response.headers['content-disposition']
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^";]+)"?/)
        if (match && match[1]) filename = match[1]
      }
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to print sticker', error)
      showAlert({
        text: error.response?.data?.message || 'Failed to print sticker',
        icon: 'error'
      })
    }
  }

  const handleDelete = async (sticker) => {
    try {
      const response = await makeRequest({
        method: 'DELETE',
        url: `/management/stickers/${sticker.stickerId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        },
        alertResult: true
      })

      if (response.status === 200) {
        setState((s) => ({
          ...s,
          stickers: s.stickers.filter(item => item.workerId !== sticker.workerId)
        }))
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleImportFromModal = async (file, isPacking) => {
    setState(s => ({ ...s, isImporting: true }))

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await makeRequest({
        method: 'POST',
        url: `/management/stickers/import/${user.farmId}?isPacking=${isPacking}`,
        data: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`,
          'Content-Type': 'multipart/form-data'
        }
      })

      if (response.status === 200) {
        showAlert({
          text: `${response.data.message}. Total imported: ${response.data.total}`,
          icon: 'success'
        })

        // Refresh the stickers list
        await getStickers()

        // Close the modal
        toggleImportModal()
      }
    } catch (error) {
      console.error('Import failed:', error)
      showAlert({
        text: error.response?.data?.message || 'Failed to import PDF stickers',
        icon: 'error'
      })
    } finally {
      setState(s => ({ ...s, isImporting: false }))
    }
  }

  const toggleImportModal = () => {
    setState(s => ({ ...s, isOpenImportModal: !s.isOpenImportModal }))
  }

  const toggleStickersModal = (selectedSticker) => {
    setState(s => ({
      ...s,
      isOpenStickersModal: !state.isOpenStickersModal,
      selectedSticker
    }))
  }

  const filteredStickers = filterRowsBySearch(state.stickers, state.searchTerm)

  const stickersTableData = {
    columns: [
      { key: 'stickerId', label: '#' },
      { key: 'workerName', label: 'Worker ' }, // Worker Name
      { key: 'containerName', label: 'Container' }, // Container Name
      { key: 'containerMaterial', label: 'Material' },
      { key: 'grossWeight', label: 'Wgt/Unit (kg)' }, // Weight per Unit (Kg)
      { key: 'reusePolicy', label: 'Reuse' },
      { key: 'resetEvery', label: 'Reset' }, // Reset Every
      { key: 'resetTime', label: 'Reset Time' }, // Daily Reset Time
      { key: 'ranges', label: 'Ranges' },
      { key: 'quantity', label: 'Qty' },
      { key: 'stickerType', label: 'Type' }, // Stickers Type
      { key: 'secondsToClose', label: 'Close (s)' }, // Seconds To Close
      {
        key: 'actions',
        label: 'Manage',
        cell: (value, sticker) => (
          <div className='flex items-center gap-2'>
            <button onClick={() => handlePrintSticker(sticker)} title='Print' className='p-1 rounded transition'>
              <PrinterIcon className='w-5 h-5 text-green-600' />
            </button>
            <button onClick={() => toggleStickersModal(sticker)} title='Edit' className='p-1 rounded transition'>
              <PencilSquareIcon className='w-5 h-5 text-blue-500' />
            </button>
            <button onClick={() => handleDelete(sticker)} title='Delete' className='p-1 rounded transition'>
              <TrashIcon className='w-5 h-5 text-red-500' />
            </button>
          </div>
        )
      }
    ],
    rows: filteredStickers
  }

  useEffect(() => {
    if (!user?.farmId) return
    if (hasFetched.current) return
    hasFetched.current = true

    Promise.all([
      getStickers(),
      getContainers(),
      getWorkers()
    ]).finally(() => {
      setState(s => ({ ...s, loading: false }))
    })
  }, [user?.farmId])

  if (state.loading) {
    return <PageLoadingState message='Loading stickers...' rows={8} />
  }

  return (
    <>
      <ActionCardComponent>
        <input
          type='text'
          placeholder='Search all fields...'
          value={state.searchTerm}
          onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className='search-input'
          data-align='left'
        />

        <ButtonComponent onClick={() => toggleStickersModal(null)}>
          <PlusIcon className='w-5 h-5 mr-1' />
          New Sticker
        </ButtonComponent>

        <ButtonComponent
          onClick={toggleImportModal}
          disabled={state.isImporting}
          className={state.isImporting ? 'opacity-50 cursor-not-allowed' : ''}
        >
          <DocumentArrowUpIcon className='w-5 h-5' />
          <span>Import PDF</span>
        </ButtonComponent>

        <ButtonComponent onClick={() => exportDataEditExcel({
          rows: filteredStickers,
          columns: stickerExportColumns,
          fileName: `stickers-${new Date().toISOString().slice(0, 10)}`,
          sheetName: 'Stickers'
        })}>
          <DocumentArrowDownIcon className='w-5 h-5' />
          <span>Export as Excel</span>
        </ButtonComponent>
        <ButtonComponent onClick={() => exportDataEditPdf({
          rows: filteredStickers,
          columns: stickerExportColumns,
          fileName: `stickers-${new Date().toISOString().slice(0, 10)}`,
          title: 'Stickers'
        })}>
          <DocumentArrowDownIcon className='w-5 h-5' />
          <span>Export as PDF</span>
        </ButtonComponent>
      </ActionCardComponent>      {/* stickers table */}
      <TableComponent
        tableData={stickersTableData}
        defaultSortColumn={stickersTableData.columns[0]?.key}
      />

      {/* modal add/edit sticker */}
      <StickersModal
        isOpen={state.isOpenStickersModal}
        onClose={toggleStickersModal}
        onSave={onSave}
        selectedSticker={state.selectedSticker}
        stickerTypes={stickerTypes}
        reusePolicies={reusePolicies}
        containers={state.containers}
        workers={state.workers}
      />

      {/* modal import stickers */}
      <ImportStickersModal
        isOpen={state.isOpenImportModal}
        onClose={toggleImportModal}
        onImport={handleImportFromModal}
        isImporting={state.isImporting}
      />
    </>
  )
}
